import { route } from 'preact-router';

/**
 * Article link helpers. Pure functions, no module state.
 *
 * Slug rule mirrors backend/lib/article-page.js: encodeURIComponent(title).toLowerCase().
 * The server page (/article/<slug>/<uuidv4>) and the hash route (#/article/<slug>/<uuidv4>)
 * share the same path, so one builder serves both.
 */

export function articleSlug(title) {
    if (title === undefined || title === null) return '';
    return encodeURIComponent(String(title)).toLowerCase();
}

export function articleRoutePath({ title, uuidv4 } = {}) {
    return `/article/${articleSlug(title)}/${uuidv4}`;
}

export function articlePagePath(article) {
    return articleRoutePath(article);
}

export function findArticleById(articles, id) {
    if (!Array.isArray(articles)) return undefined;
    const wanted = parseInt(id, 10);
    return articles.find(a => a && parseInt(a.id, 10) === wanted);
}

// featureFlags.articlePagesCategories on the domain customer: comma-separated categories
// that get a public page. Mirrors backend/lib/article-page-customer.js; empty = every article.
export function articlePageCategories(appState) {
    const value = appState?.currentCustomer?.featureFlags?.articlePagesCategories;
    if (typeof value !== 'string') return [];
    return value.split(',').map(c => c.trim().toLowerCase()).filter(Boolean);
}

export function shouldOpenArticlePage(appState, article) {
    const flag = appState?.currentCustomer?.featureFlags?.articlePages;
    if (!(flag === 1 || flag === true)) return false;
    const categories = articlePageCategories(appState);
    if (categories.length === 0) return true;
    const category = typeof article?.category === 'string' ? article.category.toLowerCase() : '';
    return category !== '' && categories.includes(category);
}

/**
 * Open an article: the page route when the domain customer has articlePages and the
 * article is known with a uuidv4, otherwise the viewArticle drawer exactly as before.
 * `articles` is the caller's own rendered list (component state); lists loaded with
 * skipUpdate never reach articleStore.articles, so it is checked first.
 * `drawerProps` are the props the caller used to pass to openDrawer (height, title,
 * editFunction, all, ...). Returns 'route' or 'drawer' for callers and tests.
 */
export function openArticle({
    stores, article, id, articles, drawerLevel = 1, drawerProps = {}, navigate = route,
}) {
    const { appState, articleStore } = stores;
    const resolvedId = article ? article.id : id;
    const resolved = article || findArticleById(articles, id) || findArticleById(articleStore?.articles, id);
    if (resolved?.uuidv4 && shouldOpenArticlePage(appState, resolved)) {
        navigate(articleRoutePath(resolved));
        return 'route';
    }
    appState.openDrawer('viewArticle', { id: resolvedId, ...drawerProps }, drawerLevel + 1);
    return 'drawer';
}
